import TranslateIcon from "@mui/icons-material/Translate";
import { Chip, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import { CURRENCY } from "../../../constants/Variables";
import {
  getAdminSetting,
  updateAdminSetting,
} from "../../../services/adminSettingApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { translateText } from "../../../utils/translationService";
import {
  addEditPregnancyWeekValidation,
  addEditSocialMediaLinksValidation,
} from "../../../utils/Validation";

const initialValues = {
  tax: 0,
  shippingFee: 0,
  expressShippingFee: 0,
  standardShippingFeeText_en: "",
  standardShippingFeeText_ro: "",
  expressShippingFeeText_en: "",
  expressShippingFeeText_ro: "",
  platformFee: 0,
  ecomPlatformFee: 0,
  ecomPercentage: 0,
  userCommission: 0,
  donationPercentage: 0,
  promotionalMessage_en: "",
  promotionalMessage_ro: "",
  promotionalLinkText_en: "",
  promotionalLinkText_ro: "",
  promotionalTextLink_en: "",
  promotionalTextLink_ro: "",
  facebookPageLink: "",
  instagramLink: "",
  youtubeLink: "",
  linkedinLink: "",
  twitterLink: "",
  tiktokLink: "",
  pregnancyWeekHighlightsTitle_en: "",
  pregnancyWeekHighlightsTitle_ro: "",
  pregnancyWeekHighlightsLink_en: "",
  pregnancyWeekHighlightsLink_ro: "",
};

const Control = () => {
  const { t } = useTranslation(["setting", "common"]);
  const [isLoading, setIsLoading] = useState({
    save: false,
    saveSocialMediaLinks: false,
    savePregnancyWeekHighlights: false,
    translate: false,
    fetch: false,
  });
  const [setting, setSetting] = useState(null);
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialValues);

  useEffect(() => {
    if (setting) {
      setInputValues({
        tax: setting?.tax || 0,
        shippingFee: setting?.shippingFee || 0,
        expressShippingFee: setting?.expressShippingFee || 0,
        platformFee: setting?.platformFee || 0,
        ecomPlatformFee: setting?.platformFeeEcom || 0,
        ecomPercentage: setting?.ecomPercentage || 0,
        userCommission: setting?.userCommission || 0,
        donationPercentage: setting?.donationPercentage || 0,
        standardShippingFeeText_en: setting?.standardShippingFeeText?.en || 0,
        standardShippingFeeText_ro: setting?.standardShippingFeeText?.ro || 0,
        expressShippingFeeText_en: setting?.expressShippingFeeText?.en || 0,
        expressShippingFeeText_ro: setting?.expressShippingFeeText?.ro || 0,
        promotionalMessage_en: setting?.promotionalNavbar?.text?.en || "",
        promotionalMessage_ro: setting?.promotionalNavbar?.text?.ro || "",
        promotionalLinkText_en: setting?.promotionalNavbar?.linkText?.en || "",
        promotionalLinkText_ro: setting?.promotionalNavbar?.linkText?.ro || "",
        promotionalTextLink_en: setting?.promotionalNavbar?.link?.en || "",
        promotionalTextLink_ro: setting?.promotionalNavbar?.link?.ro || "",
        facebookPageLink: setting?.socialLinks?.facebook || "",
        instagramLink: setting?.socialLinks?.instagram || "",
        youtubeLink: setting?.socialLinks?.youtube || "",
        linkedinLink: setting?.socialLinks?.linkedin || "",
        twitterLink: setting?.socialLinks?.twitter || "",
        tiktokLink: setting?.socialLinks?.tiktok || "",
        pregnancyWeekHighlightsTitle_en:
          setting?.pregnancyWeekHighlights?.title?.en || "",
        pregnancyWeekHighlightsTitle_ro:
          setting?.pregnancyWeekHighlights?.title?.ro || "",
        pregnancyWeekHighlightsLink_en:
          setting?.pregnancyWeekHighlights?.link?.en || "",
        pregnancyWeekHighlightsLink_ro:
          setting?.pregnancyWeekHighlights?.link?.ro || "",
      });
    }
  }, [setting]);

  useEffect(() => {
    fetchAdminSetting();
  }, []);

  const fetchAdminSetting = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, fetch: true }));
      const response = await getAdminSetting();
      if (response.data.status === "success") {
        const data = response.data.data;
        setSetting(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const updateSetting = async () => {
    try {
      const payload = {
        tax: inputValues.tax,
        shippingFee: inputValues.shippingFee,
        expressShippingFee: inputValues.expressShippingFee,
        platformFee: inputValues.platformFee,
        ecomPercentage: inputValues.ecomPercentage,
        platformFeeEcom: inputValues.ecomPlatformFee,
        userCommission: inputValues?.userCommission,
        donationPercentage: inputValues?.donationPercentage,
        standardShippingFeeText: {
          en: inputValues.standardShippingFeeText_en,
          ro: inputValues.standardShippingFeeText_ro,
        },
        expressShippingFeeText: {
          en: inputValues.expressShippingFeeText_en,
          ro: inputValues.expressShippingFeeText_ro,
        },
        promotionalNavbar: {
          isShow: true,
          text: {
            en: inputValues.promotionalMessage_en,
            ro: inputValues.promotionalMessage_ro,
          },
          linkText: {
            en: inputValues.promotionalLinkText_en,
            ro: inputValues.promotionalLinkText_ro,
          },
          link: {
            en: inputValues.promotionalTextLink_en,
            ro: inputValues.promotionalTextLink_ro,
          },
        },
      };

      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await updateAdminSetting(setting?._id, payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAdminSetting();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const updateSocialMediaLinks = async () => {
    const payload = {
      socialLinks: {
        facebook: inputValues.facebookPageLink,
        instagram: inputValues.instagramLink,
        youtube: inputValues.youtubeLink,
        linkedin: inputValues.linkedinLink,
        twitter: inputValues.twitterLink,
        tiktok: inputValues.tiktokLink,
      },
    };

    const valid = addEditSocialMediaLinksValidation(inputValues, setInputError);
    if (valid)
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );

    try {
      setIsLoading((prev) => ({ ...prev, saveSocialMediaLinks: true }));
      const response = await updateAdminSetting(setting?._id, payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAdminSetting();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, saveSocialMediaLinks: false }));
    }
  };

  const updatePregnancyWeekHighlights = async () => {
    const payload = {
      pregnancyWeekHighlights: {
        title: {
          en: inputValues.pregnancyWeekHighlightsTitle_en,
          ro: inputValues.pregnancyWeekHighlightsTitle_ro,
        },
        link: {
          en: inputValues.pregnancyWeekHighlightsLink_en,
          ro: inputValues.pregnancyWeekHighlightsLink_ro,
        },
      },
    };

    const valid = addEditPregnancyWeekValidation(inputValues, setInputError);
    if (valid)
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );

    try {
      setIsLoading((prev) => ({ ...prev, savePregnancyWeekHighlights: true }));
      const response = await updateAdminSetting(setting?._id, payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAdminSetting();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, savePregnancyWeekHighlights: false }));
    }
  };

  const handleChangeInputs = (event) => {
    const { name, value, type, valueAsNumber } = event.target;

    const finalValue = type === "number" ? valueAsNumber : value;

    if ((name === "tax" || name === "ecomPercentage") && finalValue > 100)
      return;

    setInputValues((val) => ({ ...val, [name]: finalValue }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle translate to english
  const handleTranslateToEnglish = async (key1, key2) => {
    if (!inputValues[key1]) {
      return handleOpenSnackBar(
        "error",
        t("validation.valueRequired", { ns: "common" }),
      );
    }

    setIsLoading((prev) => ({ ...prev, translate: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translate: false }));
    }
  };

  return (
    <>
      <AppLoader
        isVisible={
          isLoading.save ||
          isLoading.fetch ||
          isLoading.saveSocialMediaLinks ||
          isLoading.savePregnancyWeekHighlights
        }
      />
      <PaperContainer title={t("control")} subtitle={t("viewEditTaxShipping")}>
        <Grid container spacing={2} alignItems={"flex-end"}>
          <Grid item xs={12} md={4}>
            <Label labelText={t("tax")} required htmlFor={"tax"} />
            <TextInput
              id={"tax"}
              type={"number"}
              placeholder={`${t("enter")} ${t("enterTaxPercentage")}`}
              InputEndIcon={"%"}
              value={inputValues.tax}
              name={"tax"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("adminEcommerceCommissionPercentage")}
              required
              htmlFor={"admin-ecommerce-commission-percentage"}
            />
            <TextInput
              id={"ecom-per"}
              type={"number"}
              placeholder={`${t("enter")} ${t(
                "adminEcommerceCommissionPercentage",
              )}`}
              InputEndIcon={"%"}
              value={inputValues.ecomPercentage}
              name={"ecomPercentage"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("servicesPlatformFee")}
              required
              htmlFor={"Platform-Fee"}
            />
            <TextInput
              id={"Services Platform-Fee"}
              type={"number"}
              placeholder={`${t("enter")} ${t("servicesPlatformFee")}`}
              InputEndIcon={"RON"}
              value={inputValues.platformFee}
              name={"platformFee"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("ecommercePlatformFee")}
              required
              htmlFor={"Ecommere-Platform-Fee"}
            />
            <TextInput
              type={"number"}
              placeholder={`${t("enter")} ${t("ecommercePlatformFee")}`}
              InputEndIcon={"RON"}
              value={inputValues.ecomPlatformFee}
              name={"ecomPlatformFee"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("userCommission")}
              required
              htmlFor={"user-comission"}
            />
            <TextInput
              type={"number"}
              placeholder={`${t("enter")} ${t("userCommission")}`}
              InputEndIcon={"%"}
              value={inputValues.userCommission}
              name={"userCommission"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("donationPercentage")}
              required
              htmlFor={"donation-percentage"}
            />
            <TextInput
              type={"number"}
              placeholder={t("enterDonationPercentage")}
              InputEndIcon={"%"}
              value={inputValues.donationPercentage}
              name={"donationPercentage"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">{t("shippingInformation")}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("standardshippingFee")}
              requiredi
              htmlFor={"standardshippingFee"}
            />
            <TextInput
              id={"standardshippingFee"}
              type={"number"}
              placeholder={t("enterStandardShippingFee")}
              InputEndIcon={CURRENCY}
              value={inputValues.shippingFee}
              name={"shippingFee"}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("expresshippingFee")}
              required
              htmlFor={"expressshippingFee"}
            />
            <TextInput
              id={"expressshippingFee"}
              type={"number"}
              placeholder={t("enterExpressShippingFee")}
              InputEndIcon={CURRENCY}
              value={inputValues.expressShippingFee}
              name={"expressShippingFee"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.expressShippingFee)}
              helperText={t(inputError.expressShippingFee)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("standardFeesTextInRomanian")}
              required
              htmlFor={"standardFeesTextInRomanian"}
            />
            <TextInput
              id={"standardFeesTextInRomanian"}
              type={"text"}
              placeholder={t("enterStandardFeesTextInRomanian")}
              value={inputValues.standardShippingFeeText_ro}
              name={"standardShippingFeeText_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.standardShippingFeeText_ro)}
              helperText={t(inputError.standardShippingFeeText_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("standardFeesTextInEnglish")}
                required
                htmlFor={"standardFeesTextInEnglish"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish(
                    "standardShippingFeeText_ro",
                    "standardShippingFeeText_en",
                  )
                }
                icon={
                  isLoading.translate ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translate}
              />
            </Stack>
            <TextInput
              id={"standardFeesTextInEnglish"}
              type={"text"}
              placeholder={t("enterStandardFeesTextInEnglish")}
              value={inputValues.standardShippingFeeText_en}
              name={"standardShippingFeeText_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.standardShippingFeeText_en)}
              helperText={t(inputError.standardShippingFeeText_en)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("expressFessTaxInRomanian")}
              required
              htmlFor={"expressFessTaxInRomanian"}
            />
            <TextInput
              id={"expressFessTaxInRomanian"}
              type={"text"}
              placeholder={t("enterExpressFessTaxInRomanian")}
              value={inputValues.expressShippingFeeText_ro}
              name={"expressShippingFeeText_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.expressShippingFeeText_ro)}
              helperText={t(inputError.expressShippingFeeText_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("expressFessTaxInEnglish")}
                required
                htmlFor={"expressFessTaxInEnglish"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish(
                    "expressShippingFeeText_ro",
                    "expressShippingFeeText_en",
                  )
                }
                icon={
                  isLoading.translate ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translate}
              />
            </Stack>
            <TextInput
              id={"expressFessTaxInEnglish"}
              type={"text"}
              placeholder={t("enterExpressFessTaxInEnglish")}
              value={inputValues.expressShippingFeeText_en}
              name={"expressShippingFeeText_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.expressShippingFeeText_en)}
              helperText={t(inputError.expressShippingFeeText_en)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">{t("promotionalMessage")}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("promotionalMessageInRomanian")}
              required
              htmlFor={"promotionalMessage_ro"}
            />
            <TextInput
              id={"promotionalMessage_ro"}
              type={"text"}
              placeholder={t("enterPromotionalMessageInRomanian")}
              value={inputValues.promotionalMessage_ro}
              name={"promotionalMessage_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalMessage_ro)}
              helperText={t(inputError.promotionalMessage_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("promotionalMessageInEnglish")}
                required
                htmlFor={"promotionalMessage_en"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish(
                    "promotionalMessage_ro",
                    "promotionalMessage_en",
                  )
                }
                icon={
                  isLoading.translate ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translate}
              />
            </Stack>
            <TextInput
              id={"promotionalMessage_en"}
              type={"text"}
              placeholder={t("enterPromotionalMessageInEnglish")}
              value={inputValues.promotionalMessage_en}
              name={"promotionalMessage_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalMessage_en)}
              helperText={t(inputError.promotionalMessage_en)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("promotionalLinkTextInRomanian")}
              required
              htmlFor={"promotionalLinkText_ro"}
            />
            <TextInput
              id={"promotionalLinkText_ro"}
              type={"text"}
              placeholder={t("enterPromotionalLinkTextInRomanian")}
              value={inputValues.promotionalLinkText_ro}
              name={"promotionalLinkText_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalLinkText_ro)}
              helperText={t(inputError.promotionalLinkText_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack
              mb={"5px"}
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Label
                labelText={t("promotionalLinkTextInEnglish")}
                required
                htmlFor={"promotionalLinkText_en"}
                labelProps={{
                  marginBottom: 0,
                }}
              />
              <Chip
                label={t("translateToEnglish", { ns: "common" })}
                size="small"
                onClick={() =>
                  handleTranslateToEnglish(
                    "promotionalLinkText_ro",
                    "promotionalLinkText_en",
                  )
                }
                icon={
                  isLoading.translate ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <TranslateIcon />
                  )
                }
                disabled={isLoading.translate}
              />
            </Stack>
            <TextInput
              id={"promotionalLinkText_en"}
              type={"text"}
              placeholder={t("enterPromotionalLinkTextInEnglish")}
              value={inputValues.promotionalLinkText_en}
              name={"promotionalLinkText_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalLinkText_en)}
              helperText={t(inputError.promotionalLinkText_en)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("promotionalTextLinkInRomanian")}
              required
              htmlFor={"promotionalTextLink_ro"}
            />
            <TextInput
              id={"promotionalTextLink_ro"}
              type={"text"}
              placeholder={t("enterPromotionalTextLinkInRomanian")}
              value={inputValues.promotionalTextLink_ro}
              name={"promotionalTextLink_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalTextLink_ro)}
              helperText={t(inputError.promotionalTextLink_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("promotionalTextLinkInEnglish")}
              required
              htmlFor={"promotionalTextLink_en"}
            />
            <TextInput
              id={"promotionalTextLink_en"}
              type={"text"}
              placeholder={t("enterPromotionalTextLinkInEnglish")}
              value={inputValues.promotionalTextLink_en}
              name={"promotionalTextLink_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.promotionalTextLink_en)}
              helperText={t(inputError.promotionalTextLink_en)}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack alignItems={"flex-end"} mt={3}>
              <GlobalButton
                variant={"contained"}
                btnText={"update"}
                handleClickBtn={updateSetting}
                styleProps={{ width: 150, height: 40 }}
              />
            </Stack>
          </Grid>

          {/* Social Media Links */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("socialMediaLinks")}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("facebookPageLink")}
              required
              htmlFor={"facebookPageLink"}
            />
            <TextInput
              id={"facebookPageLink"}
              type={"text"}
              placeholder={t("enterFacebookPageLink")}
              value={inputValues.facebookPageLink}
              name={"facebookPageLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.facebookPageLink)}
              helperText={t(inputError.facebookPageLink)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("instagramLink")}
              required
              htmlFor={"instagramLink"}
            />
            <TextInput
              id={"instagramLink"}
              type={"text"}
              placeholder={t("enterInstagramLink")}
              value={inputValues.instagramLink}
              name={"instagramLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.instagramLink)}
              helperText={t(inputError.instagramLink)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("youtubeLink")}
              required
              htmlFor={"youtubeLink"}
            />
            <TextInput
              id={"youtubeLink"}
              type={"text"}
              placeholder={t("enterYoutubeLink")}
              value={inputValues.youtubeLink}
              name={"youtubeLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.youtubeLink)}
              helperText={t(inputError.youtubeLink)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("linkedinLink")}
              required
              htmlFor={"linkedinLink"}
            />
            <TextInput
              id={"linkedinLink"}
              type={"text"}
              placeholder={t("enterLinkedInLink")}
              value={inputValues.linkedinLink}
              name={"linkedinLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.linkedinLink)}
              helperText={t(inputError.linkedinLink)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("twitterLink")}
              required
              htmlFor={"twitterLink"}
            />
            <TextInput
              id={"twitterLink"}
              type={"text"}
              placeholder={t("enterTwitterLink")}
              value={inputValues.twitterLink}
              name={"twitterLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.twitterLink)}
              helperText={t(inputError.twitterLink)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("tiktokLink")}
              required
              htmlFor={"tiktokLink"}
            />
            <TextInput
              id={"tiktokLink"}
              type={"text"}
              placeholder={t("enterTiktokLink")}
              value={inputValues.tiktokLink}
              name={"tiktokLink"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.tiktokLink)}
              helperText={t(inputError.tiktokLink)}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack alignItems={"flex-end"} mt={3}>
              <GlobalButton
                variant={"contained"}
                btnText={"update"}
                handleClickBtn={updateSocialMediaLinks}
                styleProps={{ width: 150, height: 40 }}
              />
            </Stack>
          </Grid>

          {/* Pregnancy Week Highlights */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("pregnancyWeekHighlights")}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("enterPregnancyWeekHighlightsInRomanian")}
              required
              htmlFor={"pregnancyWeekHighlightsTitle_ro"}
            />
            <TextInput
              id={"pregnancyWeekHighlightsTitle_ro"}
              type={"text"}
              placeholder={t("enterPregnancyWeekHighlightsInRomanian")}
              value={inputValues.pregnancyWeekHighlightsTitle_ro}
              name={"pregnancyWeekHighlightsTitle_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.pregnancyWeekHighlightsTitle_ro)}
              helperText={t(inputError.pregnancyWeekHighlightsTitle_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("enterPregnancyWeekHighlightsInEnglish")}
              required
              htmlFor={"pregnancyWeekHighlightsTitle_en"}
            />
            <TextInput
              id={"pregnancyWeekHighlightsTitle_en"}
              type={"text"}
              placeholder={t("enterPregnancyWeekHighlightsInEnglish")}
              value={inputValues.pregnancyWeekHighlightsTitle_en}
              name={"pregnancyWeekHighlightsTitle_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.pregnancyWeekHighlightsTitle_en)}
              helperText={t(inputError.pregnancyWeekHighlightsTitle_en)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Label
              labelText={t("enterLinkInRomanian")}
              required
              htmlFor={"enterLinkInRomanian"}
            />
            <TextInput
              id={"enterLinkInRomanian"}
              type={"text"}
              placeholder={t("enterLinkInRomanian")}
              value={inputValues.pregnancyWeekHighlightsLink_ro}
              name={"pregnancyWeekHighlightsLink_ro"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.pregnancyWeekHighlightsLink_ro)}
              helperText={t(inputError.pregnancyWeekHighlightsLink_ro)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Label
              labelText={t("enterLinkInEnglish")}
              required
              htmlFor={"enterLinkInEnglish"}
            />
            <TextInput
              id={"enterLinkInEnglish"}
              type={"text"}
              placeholder={t("enterLinkInEnglish")}
              value={inputValues.pregnancyWeekHighlightsLink_en}
              name={"pregnancyWeekHighlightsLink_en"}
              onChange={handleChangeInputs}
              inputError={Boolean(inputError.pregnancyWeekHighlightsLink_en)}
              helperText={t(inputError.pregnancyWeekHighlightsLink_en)}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack alignItems={"flex-end"} mt={3}>
              <GlobalButton
                variant={"contained"}
                btnText={"update"}
                handleClickBtn={updatePregnancyWeekHighlights}
                styleProps={{ width: 150, height: 40 }}
              />
            </Stack>
          </Grid>
        </Grid>
      </PaperContainer>
    </>
  );
};

export default Control;
